import { useEffect, useMemo, useRef, useState } from "react";
import {
  ITEM_PREFIX,
  type Group,
  type Item,
} from "../replicache/keys";
import { useReplicache, useSubscribe } from "../replicache/context";
import { uuidv7 } from "../replicache/uuid";
import { useSelection } from "./selection";
import {
  PX_PER_DAY,
  addDays,
  axisTicks,
  classifyItem,
  daysBetween,
  estimateTitlePx,
  formatISODate,
  parseISODate,
  stackLanePx,
  todayLocal,
  xToDate,
  type DatedItem,
  type Placed,
  type Zoom,
} from "./ganttLayout";

const TIME_AXIS_HEIGHT = 44;
const ROW_HEIGHT = 22;
const LANE_PADDING_Y = 6;
const LANE_LABEL_WIDTH = 132;
const BAR_HEIGHT = ROW_HEIGHT - 4;
// Mobile only: when we hide the sticky fading lane label, the group
// name moves into a header row above the lane.  Reserve this much
// vertical space for it within the lane's total height.
const GROUP_LABEL_HEIGHT = 20;
const CLICK_DISTANCE_PX = 5;
const LONG_PRESS_MS = 450;
const LONG_PRESS_CANCEL_PX = 8;
const DOUBLE_TAP_MS = 350;

const ZOOM_KEY = "johndo:ganttZoom";
const PARKING_KEY = "johndo:parkingCollapsed";
const HIDDEN_GROUPS_KEY = "johndo:ganttHiddenGroups";

// Tailwind's `sm:` breakpoint.  Below this we consider the user on a
// phone-class device and restrict the zoom options to the two that make
// sense for a small screen.
const MOBILE_BREAKPOINT_QUERY = "(max-width: 639px)";
const MOBILE_ZOOMS: Zoom[] = ["full-day", "day"];
const DESKTOP_ZOOMS: Zoom[] = ["full-day", "day", "week", "month", "quarter"];

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(isMobileViewport);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return mobile;
}

// ---------------------------------------------------------------------------
// Drag state
// ---------------------------------------------------------------------------

type DragKind = "move" | "resize-start" | "resize-end" | "parking";

type DragState = {
  kind: DragKind;
  itemId: string;
  // Snapshot of dates / group at drag start.  Edits are computed against this
  // so the cursor doesn't accumulate rounding error mid-drag.
  snapshot: {
    startDate: string | null;
    dueDate: string | null;
    groupId: string;
  };
  pointerOrigin: { x: number; y: number };
  current: { x: number; y: number };
  pointerId: number;
};

type LaneLayout = {
  group: Group;
  placed: Placed[];
  rowCount: number;
  height: number;
  top: number;
};

type Preview = {
  startDate: string | null;
  dueDate: string | null;
  groupId: string;
  // True only for parking-lot drags. While dragging from the parking lot, the
  // item isn't yet on the chart; we render a floating ghost instead.
  isParkingGhost: boolean;
};

// ---------------------------------------------------------------------------
// Gantt
// ---------------------------------------------------------------------------

export function Gantt({
  groups,
  focusMode,
  onFocusMode,
}: {
  groups: Group[];
  /** Shared with the rest of the app via Home — Gantt no longer owns the
   *  state.  The FilterBar still surfaces a local toggle for ergonomics. */
  focusMode: boolean;
  onFocusMode: (v: boolean) => void;
}) {
  const rep = useReplicache();

  const items = useSubscribe<Item[]>(
    rep,
    async (tx) => {
      const rows: Item[] = [];
      for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
        const item = value as Item;
        if (item.type === "header") continue;
        rows.push(item);
      }
      return rows;
    },
    [],
  );

  // --- Filter state -------------------------------------------------------

  const isMobile = useIsMobile();

  // The user's chosen zoom is persisted across sessions; on first ever
  // visit we pick a device-appropriate default (full-day for phones,
  // week for everyone else).  When the *current* device can't show
  // the stored value (e.g. desktop-saved "week" then opened on mobile),
  // we clamp at render time without mutating storage — flipping back
  // to desktop later restores the saved choice.
  const [storedZoom, setStoredZoom] = useState<Zoom>(() => {
    const stored = localStorage.getItem(ZOOM_KEY) as Zoom | null;
    if (stored) return stored;
    return isMobileViewport() ? "full-day" : "week";
  });
  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, storedZoom);
  }, [storedZoom]);
  const availableZooms = isMobile ? MOBILE_ZOOMS : DESKTOP_ZOOMS;
  const zoom: Zoom = availableZooms.includes(storedZoom)
    ? storedZoom
    : availableZooms[0]!;
  const setZoom = setStoredZoom;
  // The "Today" view has its own rendering path with no chart lanes,
  // so the row-above lane-label convention only applies on phone-class
  // viewports for the other zooms.
  const mobileLayout = isMobile && zoom !== "full-day";

  // Day pointer for the Today view.  Reset to today every time the
  // user re-enters full-day mode so the entry experience is "show me
  // today" rather than "wherever I left off three sessions ago".
  const [selectedDay, setSelectedDay] = useState<Date>(todayLocal);
  useEffect(() => {
    if (zoom === "full-day") setSelectedDay(todayLocal());
  }, [zoom]);

  const [showCompleted, setShowCompleted] = useState(false);

  const [parkingCollapsed, setParkingCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(PARKING_KEY) === "1";
  });
  useEffect(() => {
    localStorage.setItem(PARKING_KEY, parkingCollapsed ? "1" : "0");
  }, [parkingCollapsed]);

  // Hidden-groups filter: user can untick groups they don't want on the
  // Gantt right now.  Stored as an id array so adding a new group later
  // doesn't accidentally pre-hide it.  We tolerate hidden ids that don't
  // resolve to a current group (deleted/moved) — they're just no-ops.
  const [hiddenGroupIds, setHiddenGroupIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_GROUPS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem(
      HIDDEN_GROUPS_KEY,
      JSON.stringify([...hiddenGroupIds]),
    );
  }, [hiddenGroupIds]);
  const toggleGroupHidden = (id: string) => {
    setHiddenGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Focus mode: when on, the Gantt collapses to just starred items + the
  // parent chain that gives them context, across EVERY accessible group
  // (hidden-groups filter is bypassed).  Groups end up acting as visual
  // dividers via the existing lane labels.  State lives in Home (shared
  // with TreeView's focus mode); passed in as a prop.

  // Set of item ids that pass the focus filter: every starred item plus
  // every ancestor up the parent_id chain (so the user sees what the
  // starred task is part of).  Returns null when focus mode is off so
  // call sites can skip the filter entirely.
  const focusVisibleIds: Set<string> | null = useMemo(() => {
    if (!focusMode) return null;
    const byId = new Map<string, Item>();
    for (const it of items) byId.set(it.id, it);
    const out = new Set<string>();
    for (const it of items) {
      if (!it.isFocused) continue;
      let cur: Item | undefined = it;
      const guard = new Set<string>();
      while (cur && !guard.has(cur.id)) {
        guard.add(cur.id);
        out.add(cur.id);
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
    }
    return out;
  }, [focusMode, items]);

  // Groups that survive the filter — used for lane layout, the chart
  // hit-test, and the parking-lot list.  We keep `groups` (unfiltered)
  // for the "Groups" toggle panel itself.  In focus mode we ignore the
  // user's hidden-groups toggles and instead restrict to groups that
  // actually contain a focus-visible item (an empty lane reads as noise).
  const visibleGroups = useMemo(() => {
    if (focusVisibleIds) {
      const present = new Set<string>();
      for (const it of items) {
        if (focusVisibleIds.has(it.id)) present.add(it.groupId);
      }
      return groups.filter((g) => present.has(g.id));
    }
    return groups.filter((g) => !hiddenGroupIds.has(g.id));
  }, [groups, hiddenGroupIds, focusVisibleIds, items]);

  // --- Partition by group + dated/parked ----------------------------------

  type Bucket = { dated: DatedItem[]; parked: Item[] };
  const perGroup = useMemo(() => {
    const map = new Map<string, Bucket>();
    for (const g of visibleGroups) map.set(g.id, { dated: [], parked: [] });
    for (const item of items) {
      if (item.isComplete && !showCompleted) continue;
      if (focusVisibleIds && !focusVisibleIds.has(item.id)) continue;
      const bucket = map.get(item.groupId);
      if (!bucket) continue; // item is in a hidden group → drop it from the view
      const cls = classifyItem(item);
      if (cls) bucket.dated.push(cls);
      else bucket.parked.push(item);
    }
    return map;
  }, [items, visibleGroups, showCompleted, focusVisibleIds]);

  // --- Viewport range -----------------------------------------------------

  const today = useMemo(todayLocal, []);
  const range = useMemo(() => {
    let start = addDays(today, -30);
    let end = addDays(today, 60);
    for (const { dated } of perGroup.values()) {
      for (const d of dated) {
        if (d.start.getTime() < start.getTime()) start = new Date(d.start);
        if (d.end.getTime() > end.getTime()) end = new Date(d.end);
      }
    }
    return { start: addDays(start, -7), end: addDays(end, 14) };
  }, [today, perGroup]);

  const pxPerDay = PX_PER_DAY[zoom];
  const totalDays = daysBetween(range.start, range.end);
  const chartWidth = Math.max(800, totalDays * pxPerDay);

  const dateToX = (d: Date) => daysBetween(range.start, d) * pxPerDay;
  const todayX = dateToX(today);

  const ticks = useMemo(
    () => axisTicks(range.start, range.end, zoom),
    [range, zoom],
  );

  // --- Lane layout --------------------------------------------------------

  const lanes: LaneLayout[] = useMemo(() => {
    let top = TIME_AXIS_HEIGHT;
    const out: LaneLayout[] = [];
    for (const g of visibleGroups) {
      const bucket = perGroup.get(g.id) ?? { dated: [], parked: [] };
      // Pixel-space stacking: account for titles that trail past the bar
      // by computing each item's effective right edge from bar geometry +
      // title text width.  Items whose label would overlap the next bar
      // get pushed to a separate row instead of having text collide with
      // a neighbour.
      const { placed, rowCount } = stackLanePx(
        bucket.dated,
        (d) => daysBetween(range.start, d.start) * pxPerDay,
        (d) => {
          const barLeft = daysBetween(range.start, d.start) * pxPerDay;
          const barWidth =
            d.kind === "bar"
              ? Math.max(pxPerDay, (daysBetween(d.start, d.end) + 1) * pxPerDay)
              : Math.max(14, pxPerDay);
          const barRight = barLeft + barWidth;
          const titlePx = estimateTitlePx(d.item.title || "(untitled)");
          const markerPx = d.item.priority ? 8 : 0; // bar + tiny gap
          // How much room does the bar's INSIDE provide for label content?
          // Subtract the two ~8 px resize-handle bands + a couple of px
          // of padding.  Markers (non-bar) have zero interior.
          const interior =
            d.kind === "bar" ? Math.max(0, barWidth - 24) : 0;
          const fitsInside = titlePx + markerPx + 4 <= interior;
          if (fitsInside) return barRight;
          // Label sits to the right of the bar — bar + small gap +
          // marker + label + trailing padding.
          return barRight + 6 + markerPx + titlePx + 4;
        },
      );
      const height =
        LANE_PADDING_Y * 2 +
        Math.max(1, rowCount) * ROW_HEIGHT +
        (mobileLayout ? GROUP_LABEL_HEIGHT : 0);
      out.push({ group: g, placed, rowCount, height, top });
      top += height;
    }
    return out;
  }, [visibleGroups, perGroup, range.start, pxPerDay, mobileLayout]);

  const chartHeight = lanes.length
    ? lanes[lanes.length - 1]!.top + lanes[lanes.length - 1]!.height
    : TIME_AXIS_HEIGHT + 40;

  // --- Selected item ------------------------------------------------------

  const { selectedId, select: setSelectedId, open: openDetail } = useSelection();

  // (F keyboard shortcut lives in Home's FocusHotkey component now, so a
  // single listener handles both Tree and Gantt without duplicating when
  // multiple TreeViews are stacked.)

  // --- Refs for hit testing -----------------------------------------------

  const scrollerRef = useRef<HTMLDivElement>(null);
  const chartInnerRef = useRef<HTMLDivElement>(null);
  const parkingLotRef = useRef<HTMLElement>(null);

  // Scroll-to-today on mount and on zoom change.  At full-day zoom the
  // implicit aim is "today view", so we centre today in the viewport;
  // for wider zooms we leave ~240 px of past context to the left so the
  // user sees how today sits relative to recent work.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const offset =
      zoom === "full-day"
        ? Math.max(0, (el.clientWidth - pxPerDay) / 2)
        : 240;
    el.scrollLeft = Math.max(0, todayX - offset);
  }, [zoom, todayX, pxPerDay]);

  // --- Drag handling ------------------------------------------------------

  const [drag, setDrag] = useState<DragState | null>(null);
  // Mirror in a ref so window pointermove/up handlers see current values.
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  // Last tap (per Gantt instance) for double-tap detection on bars.
  const lastTapRef = useRef<{ id: string; t: number } | null>(null);

  // Convert a viewport-space client point into chart-local coordinates,
  // accounting for the scrollable container's scroll position.
  const clientToChart = (clientX: number, clientY: number) => {
    const inner = chartInnerRef.current;
    if (!inner) return { x: 0, y: 0 };
    const rect = inner.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const isOverParkingLot = (clientX: number, clientY: number) => {
    const el = parkingLotRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return (
      clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom
    );
  };

  // Live preview while dragging — what the dragged item would look like if
  // committed right now. Returned per-item-id; lookup by id.
  const livePreview: Preview | null = useMemo(() => {
    if (!drag) return null;
    const item = items.find((i) => i.id === drag.itemId);
    if (!item) return null;

    if (drag.kind === "parking") {
      // Parking-lot drop preview: always show the date the cursor X
      // resolves to, on the item's own lane (Y is ignored — the group
      // can't change via drag).  If the cursor is over the parking lot
      // itself we leave dueDate null so the ghost reads as "no date".
      const overParking = isOverParkingLot(drag.current.x, drag.current.y);
      const { x } = clientToChart(drag.current.x, drag.current.y);
      const date = formatISODate(xToDate(x, range.start, pxPerDay));
      return {
        startDate: null,
        dueDate: overParking ? null : date,
        groupId: item.groupId,
        isParkingGhost: true,
      };
    }

    const dxDays = Math.round((drag.current.x - drag.pointerOrigin.x) / pxPerDay);
    const startDate = drag.snapshot.startDate;
    const dueDate = drag.snapshot.dueDate;

    if (drag.kind === "move") {
      // Cross-lane drag does NOT change group — the preview (and the
      // commit below) stay pinned to the snapshot's group regardless of
      // which lane the cursor is over.  Per "Gantt improvements" feature
      // ask: avoid accidental cross-group moves while dragging.
      return {
        startDate: startDate
          ? formatISODate(addDays(parseISODate(startDate), dxDays))
          : null,
        dueDate: dueDate
          ? formatISODate(addDays(parseISODate(dueDate), dxDays))
          : null,
        groupId: drag.snapshot.groupId,
        isParkingGhost: false,
      };
    }
    if (drag.kind === "resize-start") {
      // Resizing the left edge.  If the item was a due-only marker, this
      // synthesizes a startDate.
      const baseStart = startDate ? parseISODate(startDate) : parseISODate(dueDate!);
      let newStart = addDays(baseStart, dxDays);
      if (dueDate) {
        const due = parseISODate(dueDate);
        if (newStart.getTime() > due.getTime()) newStart = due;
      }
      return {
        startDate: formatISODate(newStart),
        dueDate,
        groupId: drag.snapshot.groupId,
        isParkingGhost: false,
      };
    }
    // resize-end
    const baseEnd = dueDate ? parseISODate(dueDate) : parseISODate(startDate!);
    let newDue = addDays(baseEnd, dxDays);
    if (startDate) {
      const st = parseISODate(startDate);
      if (newDue.getTime() < st.getTime()) newDue = st;
    }
    return {
      startDate,
      dueDate: formatISODate(newDue),
      groupId: drag.snapshot.groupId,
      isParkingGhost: false,
    };
  }, [drag, items, range.start, pxPerDay]);

  // Commit a drag: compute the new dates/group from the snapshot + final
  // pointer position and write to Replicache.
  //
  // Crucially, we don't read `livePreview` here — that's a useMemo whose value
  // is `null` at the moment startDrag captures this closure, and the listener
  // in startDrag keeps that stale reference for the lifetime of the drag.
  // Computing from `state.snapshot` (passed in by value) avoids the trap.
  const commitDrag = (state: DragState, finalClient: { x: number; y: number }) => {
    if (!rep) return;
    const item = items.find((i) => i.id === state.itemId);
    if (!item) return;

    const movedDistance =
      Math.abs(finalClient.x - state.pointerOrigin.x) +
      Math.abs(finalClient.y - state.pointerOrigin.y);
    if (movedDistance < CLICK_DISTANCE_PX) {
      // Pointer down + up with negligible movement = tap.  Double-tap (two
      // taps within DOUBLE_TAP_MS, same item) opens the detail pane;
      // single tap just sets the selection.  The detail pane also follows
      // selection changes (see selection.tsx), so after a double-tap the
      // pane auto-tracks subsequent single-taps on other bars.
      const last = lastTapRef.current;
      const now = Date.now();
      if (last && last.id === state.itemId && now - last.t < DOUBLE_TAP_MS) {
        openDetail(state.itemId);
        lastTapRef.current = null;
      } else {
        setSelectedId(state.itemId);
        lastTapRef.current = { id: state.itemId, t: now };
      }
      return;
    }

    const dxDays = Math.round(
      (finalClient.x - state.pointerOrigin.x) / pxPerDay,
    );
    const snapStart = state.snapshot.startDate;
    const snapDue = state.snapshot.dueDate;

    if (state.kind === "parking") {
      if (isOverParkingLot(finalClient.x, finalClient.y)) return;
      const { x } = clientToChart(finalClient.x, finalClient.y);
      // Lane Y is no longer used to assign a group — the parked item
      // keeps its existing groupId.  We just need a valid date from X
      // and the chart will place the bar on the item's own lane.
      const date = formatISODate(xToDate(x, range.start, pxPerDay));
      void rep.mutate.updateItem({ id: item.id, dueDate: date });
      return;
    }

    if (state.kind === "move") {
      // Drop on parking lot → clear both dates.
      if (isOverParkingLot(finalClient.x, finalClient.y)) {
        void rep.mutate.updateItem({
          id: state.itemId,
          startDate: null,
          dueDate: null,
        });
        return;
      }
      // Cross-lane drags no longer change group — the bar stays in its
      // own lane regardless of the cursor's Y position.  Only the
      // dates shift by the horizontal delta.
      const newStart = snapStart
        ? formatISODate(addDays(parseISODate(snapStart), dxDays))
        : null;
      const newDue = snapDue
        ? formatISODate(addDays(parseISODate(snapDue), dxDays))
        : null;
      void rep.mutate.updateItem({
        id: state.itemId,
        startDate: newStart,
        dueDate: newDue,
      });
      return;
    }

    if (state.kind === "resize-start") {
      // Anchor: the left edge of the bar (or the marker's date).  Clamp so we
      // never produce startDate > dueDate.
      const baseStart = snapStart ? parseISODate(snapStart) : parseISODate(snapDue!);
      let newStart = addDays(baseStart, dxDays);
      if (snapDue) {
        const due = parseISODate(snapDue);
        if (newStart.getTime() > due.getTime()) newStart = due;
      }
      void rep.mutate.updateItem({
        id: state.itemId,
        startDate: formatISODate(newStart),
        dueDate: snapDue,
      });
      return;
    }

    // resize-end
    const baseEnd = snapDue ? parseISODate(snapDue) : parseISODate(snapStart!);
    let newDue = addDays(baseEnd, dxDays);
    if (snapStart) {
      const st = parseISODate(snapStart);
      if (newDue.getTime() < st.getTime()) newDue = st;
    }
    void rep.mutate.updateItem({
      id: state.itemId,
      startDate: snapStart,
      dueDate: formatISODate(newDue),
    });
  };

  // Tap on a chart bar — single-tap selects, double-tap opens the detail
  // pane.  Extracted because the long-press gate below has its own
  // "tap" fall-through path that needs the same semantics.
  const handleBarTap = (itemId: string) => {
    const last = lastTapRef.current;
    const now = Date.now();
    if (last && last.id === itemId && now - last.t < DOUBLE_TAP_MS) {
      openDetail(itemId);
      lastTapRef.current = null;
    } else {
      setSelectedId(itemId);
      lastTapRef.current = { id: itemId, t: now };
    }
  };

  // Start a drag from the chart (existing bar) or parking lot.
  //
  // On touch, every drag — including parking-lot pickups — requires a
  // brief touch-and-hold before drag mode engages, so a stray finger
  // brushing past doesn't scrub a task across the timeline (or yank
  // an item out of the parking lot).  Quick taps fall through to
  // tap-to-select / double-tap-to-open via handleBarTap.
  const TOUCH_HOLD_MS = 280;
  const TOUCH_HOLD_CANCEL_PX = 8;
  const startDrag = (
    kind: DragKind,
    item: Item,
    e: React.PointerEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const initialClient = { x: e.clientX, y: e.clientY };
    const pointerId = e.pointerId;
    const target = e.target as Element;
    const requireHold = e.pointerType === "touch";

    const beginActualDrag = () => {
      target.setPointerCapture?.(pointerId);
      const state: DragState = {
        kind,
        itemId: item.id,
        snapshot: {
          startDate: item.startDate,
          dueDate: item.dueDate,
          groupId: item.groupId,
        },
        pointerOrigin: initialClient,
        current: initialClient,
        pointerId,
      };
      setDrag(state);
      dragRef.current = state;

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const next = dragRef.current;
        if (!next) return;
        const updated = {
          ...next,
          current: { x: ev.clientX, y: ev.clientY },
        };
        dragRef.current = updated;
        setDrag(updated);
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        const final = dragRef.current;
        if (final) {
          commitDrag(final, { x: ev.clientX, y: ev.clientY });
        }
        dragRef.current = null;
        setDrag(null);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    };

    if (!requireHold) {
      beginActualDrag();
      return;
    }

    // Touch on a chart bar: arm the long-press.
    let movedTooMuch = false;
    let timer: number | null = window.setTimeout(() => {
      timer = null;
      window.removeEventListener("pointermove", waitMove);
      window.removeEventListener("pointerup", waitUp);
      window.removeEventListener("pointercancel", waitUp);
      try {
        navigator.vibrate?.(25);
      } catch {
        /* not supported */
      }
      beginActualDrag();
    }, TOUCH_HOLD_MS);

    const waitMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (
        Math.abs(ev.clientX - initialClient.x) > TOUCH_HOLD_CANCEL_PX ||
        Math.abs(ev.clientY - initialClient.y) > TOUCH_HOLD_CANCEL_PX
      ) {
        movedTooMuch = true;
        if (timer !== null) {
          window.clearTimeout(timer);
          timer = null;
        }
      }
    };
    const waitUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      window.removeEventListener("pointermove", waitMove);
      window.removeEventListener("pointerup", waitUp);
      window.removeEventListener("pointercancel", waitUp);
      // Quick release without significant movement = tap.  Movement
      // beyond the cancel threshold is treated as accidental — no drag,
      // no selection change.
      if (!movedTooMuch) handleBarTap(item.id);
    };

    window.addEventListener("pointermove", waitMove);
    window.addEventListener("pointerup", waitUp);
    window.addEventListener("pointercancel", waitUp);
  };

  // Long-press on lane background → create a new task on the pressed day.
  // A tap (down + up with no significant movement, before the timer fires)
  // does nothing; a quick drag also does nothing.  This avoids accidental
  // creates from stray clicks while still giving touch users a way in.
  type Press = {
    groupId: string;
    chartX: number; // for the visual ghost
    clientStart: { x: number; y: number };
    pointerId: number;
  };
  const [press, setPress] = useState<Press | null>(null);
  const pressRef = useRef<Press | null>(null);
  pressRef.current = press;

  const onLanePointerDown = (
    e: React.PointerEvent,
    lane: LaneLayout,
  ) => {
    if (!rep) return;
    // Only fire on the lane itself, not on a descendant (label, bar, etc.).
    if (e.target !== e.currentTarget) return;
    if (e.button !== undefined && e.button !== 0) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const { x: chartX } = clientToChart(startX, startY);

    const state: Press = {
      groupId: lane.group.id,
      chartX,
      clientStart: { x: startX, y: startY },
      pointerId: e.pointerId,
    };
    setPress(state);
    pressRef.current = state;

    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      window.clearTimeout(timer);
      setPress(null);
      pressRef.current = null;
    };

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== state.pointerId) return;
      if (
        Math.abs(ev.clientX - startX) > LONG_PRESS_CANCEL_PX ||
        Math.abs(ev.clientY - startY) > LONG_PRESS_CANCEL_PX
      ) {
        cleanup();
      }
    };
    const onEnd = (ev: PointerEvent) => {
      if (ev.pointerId !== state.pointerId) return;
      cleanup();
    };

    const timer = window.setTimeout(() => {
      // Timer fired — long-press confirmed.  Stop listening and create.
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      setPress(null);
      pressRef.current = null;

      const date = formatISODate(xToDate(chartX, range.start, pxPerDay));
      const id = uuidv7();
      void (async () => {
        await rep.mutate.createItem({
          id,
          groupId: lane.group.id,
          parentId: null,
          type: "task",
          title: "",
          dueDate: date,
          sortKey: nextSortKeyFor(items, lane.group.id),
        });
        // The freshly-created task is untitled and the Gantt has no inline
        // editor; open the detail pane so the user can type a title.
        openDetail(id);
      })();
    }, LONG_PRESS_MS);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <FilterBar
        zoom={zoom}
        zooms={availableZooms}
        onZoom={setZoom}
        showCompleted={showCompleted}
        onShowCompleted={setShowCompleted}
        focusMode={focusMode}
        onFocusMode={onFocusMode}
      />

      <div className="flex flex-1 overflow-hidden">
        <ParkingLot
          containerRef={parkingLotRef}
          groups={groups}
          visibleGroups={visibleGroups}
          hiddenGroupIds={hiddenGroupIds}
          onToggleGroupHidden={toggleGroupHidden}
          focusMode={focusMode}
          perGroup={perGroup}
          onStartDrag={(item, e) => startDrag("parking", item, e)}
          dragOver={
            drag !== null &&
            (drag.kind === "move" ||
              drag.kind === "resize-start" ||
              drag.kind === "resize-end") &&
            isOverParkingLot(drag.current.x, drag.current.y)
          }
          collapsed={parkingCollapsed}
          onToggleCollapsed={() => setParkingCollapsed((c) => !c)}
        />

        {focusMode && focusVisibleIds && focusVisibleIds.size === 0 ? (
          <div className="flex flex-1 items-center justify-center bg-white p-8 text-center text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
            <div className="max-w-sm">
              <div className="text-amber-500 text-2xl">★</div>
              <p className="mt-2 font-medium text-slate-700 dark:text-slate-200">
                Focus mode is on, but nothing is starred.
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Select a task and press <kbd className="rounded border border-slate-300 bg-slate-50 px-1 dark:border-slate-600 dark:bg-slate-900">F</kbd>,
                or open a task and tap <span className="text-amber-500">★</span> Currently working on.
              </p>
            </div>
          </div>
        ) : zoom === "full-day" ? (
          <TodayView
            groups={visibleGroups}
            perGroup={perGroup}
            selectedDay={selectedDay}
            onPrev={() => setSelectedDay((d) => addDays(d, -1))}
            onNext={() => setSelectedDay((d) => addDays(d, 1))}
            onToday={() => setSelectedDay(todayLocal())}
            onSelect={(id) => setSelectedId(id)}
            onOpen={(id) => openDetail(id)}
            selectedId={selectedId}
          />
        ) : (
        <div ref={scrollerRef} className="flex-1 overflow-auto bg-white dark:bg-slate-900">
          <div
            ref={chartInnerRef}
            className="relative"
            style={{ width: chartWidth, height: chartHeight }}
          >
            {/* Time axis (sticky top) */}
            <div
              className="sticky top-0 z-30 border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
              style={{ height: TIME_AXIS_HEIGHT }}
            >
              {ticks.map((tick, i) => {
                const x = dateToX(tick.date);
                return (
                  <div key={i}>
                    <div
                      className={`absolute top-0 ${
                        tick.major
                          ? "border-l border-slate-300 dark:border-slate-600"
                          : "border-l border-slate-100 dark:border-slate-800"
                      }`}
                      style={{ left: x, height: TIME_AXIS_HEIGHT }}
                    />
                    <div
                      className={`absolute top-2 whitespace-nowrap text-[11px] ${
                        tick.major ? "font-medium text-slate-700 dark:text-slate-200" : "text-slate-500 dark:text-slate-400"
                      }`}
                      style={{ left: x + 4 }}
                    >
                      {tick.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vertical grid lines across all lanes */}
            {ticks.map((tick, i) => (
              <div
                key={`gl-${i}`}
                className={
                  tick.major
                    ? "absolute border-l border-slate-200 dark:border-slate-700"
                    : "absolute border-l border-slate-50 dark:border-slate-800"
                }
                style={{
                  left: dateToX(tick.date),
                  top: TIME_AXIS_HEIGHT,
                  height: chartHeight - TIME_AXIS_HEIGHT,
                }}
              />
            ))}

            {/* Today line */}
            {todayX >= 0 && todayX <= chartWidth && (
              <div
                className="absolute z-20 border-l-2 border-rose-500 pointer-events-none"
                style={{ left: todayX, top: 0, height: chartHeight }}
              >
                <div className="absolute left-1 top-1 rounded bg-rose-500 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                  Today
                </div>
              </div>
            )}

            {/* Highlight the item's own lane during a parking-lot drag —
                we no longer change group on drop, so guiding the eye to
                the destination lane (not the cursor lane) matches what's
                about to happen. */}
            {drag?.kind === "parking" &&
              (() => {
                const lane = lanes.find(
                  (l) => l.group.id === drag.snapshot.groupId,
                );
                if (!lane) return null;
                return (
                  <div
                    className="absolute z-10 bg-blue-100 pointer-events-none dark:bg-blue-950"
                    style={{ left: 0, top: lane.top, width: chartWidth, height: lane.height }}
                  />
                );
              })()}

            {/* Long-press feedback — a small marker grows over LONG_PRESS_MS
                so the user can see they're about to create a task. */}
            {press &&
              (() => {
                const lane = lanes.find((l) => l.group.id === press.groupId);
                if (!lane) return null;
                const ghostWidth = Math.max(pxPerDay, 14);
                const left =
                  Math.round(press.chartX / pxPerDay) * pxPerDay - ghostWidth / 2;
                return (
                  <div
                    className="absolute z-20 origin-center animate-press-grow rounded bg-blue-500/60 pointer-events-none"
                    style={{
                      left,
                      top:
                        lane.top +
                        LANE_PADDING_Y +
                        (mobileLayout ? GROUP_LABEL_HEIGHT : 0),
                      width: ghostWidth,
                      height: BAR_HEIGHT,
                    }}
                  />
                );
              })()}

            {/* Lanes */}
            {lanes.map((lane) => (
              <div
                key={lane.group.id}
                className="absolute left-0 right-0 border-b border-slate-100 cursor-cell dark:border-slate-800"
                style={{ top: lane.top, height: lane.height }}
                onPointerDown={(e) => onLanePointerDown(e, lane)}
              >
                {mobileLayout ? (
                  // Mobile: group name lives in a flat header row above
                  // the lane so the chart gets the full screen width.
                  // The row scrolls with the chart horizontally; we
                  // sticky the inner text to the viewport so it stays
                  // readable as the user pans.
                  <div
                    className="absolute left-0 right-0 top-0 flex items-center border-b border-slate-100 bg-slate-50/90 pointer-events-none dark:border-slate-800 dark:bg-slate-900/90"
                    style={{ height: GROUP_LABEL_HEIGHT }}
                  >
                    <span className="sticky left-2 truncate pr-2 text-[11px] font-medium uppercase tracking-wide text-slate-600 dark:text-slate-300">
                      {lane.group.name}
                    </span>
                  </div>
                ) : (
                  <div
                    className="sticky left-0 z-10 inline-flex h-full items-start pl-2 pr-3 pt-1 pointer-events-none [--lane-bg:255,255,255] dark:[--lane-bg:15,23,42]"
                    style={{
                      width: LANE_LABEL_WIDTH,
                      background:
                        "linear-gradient(to right, rgba(var(--lane-bg),0.95) 80%, rgba(var(--lane-bg),0))",
                    }}
                  >
                    <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                      {lane.group.name}
                    </span>
                  </div>
                )}

                {lane.placed.map((p) => (
                  <GanttItem
                    key={p.item.id}
                    placement={p}
                    dateToX={dateToX}
                    pxPerDay={pxPerDay}
                    today={today}
                    rowTop={
                      LANE_PADDING_Y +
                      p.rowIndex * ROW_HEIGHT +
                      (mobileLayout ? GROUP_LABEL_HEIGHT : 0)
                    }
                    selected={selectedId === p.item.id}
                    preview={
                      drag?.itemId === p.item.id && livePreview && !livePreview.isParkingGhost
                        ? livePreview
                        : null
                    }
                    isDragging={drag?.itemId === p.item.id}
                    onPointerDown={(e, kind) =>
                      startDrag(kind, p.item, e)
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Floating ghost while dragging from parking lot */}
      {drag?.kind === "parking" &&
        (() => {
          const item = items.find((i) => i.id === drag.itemId);
          if (!item) return null;
          return (
            <div
              className="pointer-events-none fixed z-50 rounded bg-blue-500 px-2 py-0.5 text-xs text-white shadow-lg"
              style={{ left: drag.current.x + 8, top: drag.current.y + 8 }}
            >
              {item.title || "(untitled)"}
            </div>
          );
        })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar({
  zoom,
  zooms,
  onZoom,
  showCompleted,
  onShowCompleted,
  focusMode,
  onFocusMode,
}: {
  zoom: Zoom;
  /** Which zoom buttons to render — narrowed on mobile. */
  zooms: Zoom[];
  onZoom: (z: Zoom) => void;
  showCompleted: boolean;
  onShowCompleted: (v: boolean) => void;
  focusMode: boolean;
  onFocusMode: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-4 border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600">
        {zooms.map((z) => (
          <button
            key={z}
            onClick={() => onZoom(z)}
            className={`px-2 py-1 text-xs capitalize ${
              z === zoom
                ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            }`}
            // The full-day label is two words; the rest are single tokens.
            title={z === "full-day" ? "Today view — one day fills the screen" : undefined}
          >
            {z === "full-day" ? "Today" : z}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onFocusMode(!focusMode)}
        aria-pressed={focusMode}
        title="Focus mode — show only starred tasks and their parents, grouped by source group"
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
          focusMode
            ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200 dark:hover:bg-amber-900"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        }`}
      >
        <span aria-hidden className={focusMode ? "text-amber-500" : "text-slate-400 dark:text-slate-500"}>★</span>
        Focus
      </button>
      <label className="flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200">
        <input
          type="checkbox"
          checked={showCompleted}
          onChange={(e) => onShowCompleted(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
        />
        Show completed
      </label>
      <HelpPopover className="ml-auto">
        <ul className="list-disc space-y-1 pl-4">
          <li>Drag bars to reschedule</li>
          <li>Drag a bar's edge to resize</li>
          <li>Hold on an empty lane to add a task</li>
          <li>Drag a parked item onto a lane to schedule it</li>
          <li>Press <kbd className="rounded border border-slate-300 bg-white px-1 dark:border-slate-600 dark:bg-slate-900">F</kbd> to star the selected task</li>
          <li>Toggle ★ Focus to see only starred tasks across all groups</li>
        </ul>
      </HelpPopover>
    </div>
  );
}

// Small (?) icon button that toggles an attached popover.  The popover
// is positioned bottom-right of the button so it doesn't push other
// FilterBar controls around.  Closes on outside click or Esc.
function HelpPopover({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Gantt help"
        aria-expanded={open}
        title="Gantt tips"
        className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      >
        ?
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parking lot
// ---------------------------------------------------------------------------

const ParkingLot = ({
  containerRef,
  groups,
  visibleGroups,
  hiddenGroupIds,
  onToggleGroupHidden,
  focusMode,
  perGroup,
  onStartDrag,
  dragOver,
  collapsed,
  onToggleCollapsed,
}: {
  // Named `containerRef` rather than `ref` because we're on React 18:
  // a plain `ref` prop on a function component is intercepted by React
  // and never reaches us, so the parent's parkingLotRef would stay
  // null — and isOverParkingLot would always return false, meaning
  // "drop back on parking lot to cancel" silently turned into "drop
  // onto the chart at the cursor X".  Pass-through via forwardRef
  // would also work; the explicit prop name is cheaper here.
  containerRef: React.Ref<HTMLElement>;
  /** All groups (for the toggle list). */
  groups: Group[];
  /** Subset shown on the chart (for the parked-items list). */
  visibleGroups: Group[];
  hiddenGroupIds: Set<string>;
  onToggleGroupHidden: (id: string) => void;
  /** When true, the Groups toggle list is hidden — the chart is
   *  restricted to focus-visible items and the toggles don't apply. */
  focusMode: boolean;
  perGroup: Map<string, { dated: DatedItem[]; parked: Item[] }>;
  onStartDrag: (item: Item, e: React.PointerEvent) => void;
  dragOver: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) => {
  // Total parked count across visible groups only — hidden groups
  // shouldn't pad the summary number.
  let parkedCount = 0;
  for (const g of visibleGroups) {
    parkedCount += perGroup.get(g.id)?.parked.length ?? 0;
  }

  if (collapsed) {
    return (
      <aside
        ref={containerRef}
        className={`flex w-8 shrink-0 flex-col items-center gap-1 border-r border-slate-200 dark:border-slate-700 py-2 transition-colors ${
          dragOver ? "bg-rose-50 dark:bg-rose-950" : "bg-slate-50 dark:bg-slate-900"
        }`}
        title={`Parking lot (${parkedCount})`}
      >
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label="Show parking lot"
          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          ▸
        </button>
        <div className="rotate-180 select-none text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 [writing-mode:vertical-rl]">
          Parking lot{parkedCount > 0 ? ` · ${parkedCount}` : ""}
        </div>
      </aside>
    );
  }

  return (
    <aside
      ref={containerRef}
      className={`w-56 shrink-0 overflow-y-auto border-r border-slate-200 dark:border-slate-700 p-3 transition-colors ${
        dragOver ? "bg-rose-50 dark:bg-rose-950" : "bg-slate-50 dark:bg-slate-900"
      }`}
    >
      {groups.length > 0 && !focusMode && (
        <section className="mb-4">
          <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Groups
          </h3>
          <ul className="space-y-0.5">
            {groups.map((g) => {
              const hidden = hiddenGroupIds.has(g.id);
              return (
                <li key={g.id}>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => onToggleGroupHidden(g.id)}
                      className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
                    />
                    <span
                      className={`truncate ${hidden ? "text-slate-400 dark:text-slate-500" : ""}`}
                    >
                      {g.name}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {dragOver ? "Drop to clear dates" : "Parking lot"}
        </h3>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label="Hide parking lot"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          ◂
        </button>
      </div>
      {visibleGroups.every(
        (g) => (perGroup.get(g.id)?.parked.length ?? 0) === 0,
      ) ? (
        <p className="text-xs italic text-slate-400 dark:text-slate-500">
          Items without dates show up here.
        </p>
      ) : (
        visibleGroups.map((group) => {
          const parked = perGroup.get(group.id)?.parked ?? [];
          if (parked.length === 0) return null;
          return (
            <div key={group.id} className="mb-3 last:mb-0">
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {group.name}
              </div>
              <ul className="space-y-1">
                {parked.map((item) => (
                  <li
                    key={item.id}
                    onPointerDown={(e) => onStartDrag(item, e)}
                    style={{ touchAction: "none" }}
                    className={`cursor-grab truncate rounded border border-slate-200 bg-white px-2 py-1 text-xs active:cursor-grabbing dark:border-slate-700 dark:bg-slate-900 ${
                      item.isComplete ? "text-slate-400 line-through dark:text-slate-500" : "text-slate-700 dark:text-slate-200"
                    }`}
                    title={item.title}
                  >
                    {item.title || (
                      <span className="italic text-slate-400 dark:text-slate-500">(untitled)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </aside>
  );
};

// ---------------------------------------------------------------------------
// One bar / marker
// ---------------------------------------------------------------------------

// Visible resize-handle width.  Wide enough to hit comfortably with a mouse
// and forgiving on touch.  If a bar is narrower than ~3× this, handles are
// suppressed — the user can zoom in to resize.
const RESIZE_HANDLE_W = 8;
const MIN_BAR_WIDTH_FOR_RESIZE = RESIZE_HANDLE_W * 3;

function GanttItem({
  placement,
  dateToX,
  pxPerDay,
  today,
  rowTop,
  selected,
  preview,
  isDragging,
  onPointerDown,
}: {
  placement: Placed;
  dateToX: (d: Date) => number;
  pxPerDay: number;
  today: Date;
  rowTop: number;
  selected: boolean;
  preview: Preview | null;
  isDragging: boolean;
  onPointerDown: (e: React.PointerEvent, kind: DragKind) => void;
}) {
  const { item, kind } = placement;

  // Effective dates / kind during a live drag.
  let start = placement.start;
  let end = placement.end;
  let effKind: typeof kind = kind;
  if (preview) {
    const sd = preview.startDate;
    const dd = preview.dueDate;
    if (sd && dd) {
      start = parseISODate(sd);
      end = parseISODate(dd);
      effKind = "bar";
    } else if (dd) {
      start = end = parseISODate(dd);
      effKind = "due";
    } else if (sd) {
      start = end = parseISODate(sd);
      effKind = "started";
    }
  }

  const left = dateToX(start);
  const widthPx =
    effKind === "bar"
      ? Math.max(pxPerDay, (daysBetween(start, end) + 1) * pxPerDay)
      : Math.max(14, pxPerDay);

  const overdue =
    !item.isComplete &&
    placement.kind !== "started" &&
    end.getTime() < today.getTime();

  let bg = "bg-blue-500 text-white";
  if (item.isComplete) bg = "bg-slate-300 text-slate-600 line-through";
  else if (overdue) bg = "bg-rose-500 text-white";
  else if (item.status === "in_progress") bg = "bg-emerald-500 text-white";
  else if (item.status === "blocked") bg = "bg-amber-500 text-white";
  else if (item.status === "cancelled") bg = "bg-slate-300 text-slate-600";

  const titleAttr = `${item.title || "(untitled)"} — ${item.startDate ?? "?"} → ${item.dueDate ?? "?"}`;
  const ring = selected ? "ring-2 ring-offset-1 ring-slate-900 dark:ring-slate-100" : "";

  const onDown = (kind: DragKind) => (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button !== 0) return; // primary button only
    onPointerDown(e, kind);
  };

  const titleText = item.title || "(untitled)";
  const titlePx = estimateTitlePx(titleText);

  // Markers (single-date): no resize gesture, drag = move.  Title is
  // ALWAYS outside (markers are too narrow to host a label), with the
  // priority marker between the icon and the title.
  if (effKind !== "bar") {
    return (
      <div
        style={{
          left,
          width: widthPx,
          top: rowTop,
          height: BAR_HEIGHT,
          touchAction: "none",
          opacity: isDragging ? 0.85 : 1,
        }}
        title={titleAttr}
        className="absolute"
      >
        <button
          type="button"
          onPointerDown={onDown("move")}
          className={`absolute inset-0 flex cursor-grab items-center justify-center rounded text-[11px] shadow-sm transition-colors active:cursor-grabbing ${bg} ${ring}`}
        >
          {effKind === "due" && <span aria-hidden>◆</span>}
          {effKind === "started" && <span aria-hidden>▶</span>}
        </button>
        <span
          className="pointer-events-none absolute inset-y-0 left-full ml-1 flex items-center gap-1 whitespace-nowrap text-[11px] text-slate-700 dark:text-slate-200"
        >
          <GanttPriorityMarker priority={item.priority} />
          {item.isFocused && <FocusStar />}
          <span>{titleText}</span>
        </span>
      </div>
    );
  }

  // Bar: three zones — left resize handle, body (move), right resize handle.
  // Each zone has its own onPointerDown so the cursor + gesture match what
  // the user is actually touching.  Handles hide on bars too narrow to split.
  const canResize = widthPx >= MIN_BAR_WIDTH_FOR_RESIZE;

  // How much horizontal room is available *inside* the bar for the label
  // (marker + title)?  Subtract the two resize bands (when they're drawn)
  // and a couple of px of breathing room on each side.
  const interiorPad = canResize ? RESIZE_HANDLE_W * 2 + 8 : 8;
  const interiorWidth = Math.max(0, widthPx - interiorPad);
  const markerPx = item.priority ? 8 : 0;
  const fitsInside = titlePx + markerPx + 4 <= interiorWidth;

  return (
    <div
      style={{
        left,
        width: widthPx,
        top: rowTop,
        height: BAR_HEIGHT,
        opacity: isDragging ? 0.85 : 1,
      }}
      title={titleAttr}
      // No overflow-hidden — when the label sits outside we need to
      // render past the bar's right edge.
      className={`group absolute rounded text-[11px] shadow-sm transition-colors ${ring}`}
    >
      {/* Coloured body: covers exactly widthPx with rounded corners and
          contains the 3 resize/move zones.  overflow-hidden keeps the
          handle hover-states inside the bar rect. */}
      <div
        className={`absolute inset-0 flex items-stretch overflow-hidden rounded ${bg}`}
      >
        {canResize && (
          <div
            onPointerDown={onDown("resize-start")}
            style={{ width: RESIZE_HANDLE_W, touchAction: "none" }}
            className="shrink-0 cursor-ew-resize border-r border-black/10 bg-black/10 transition-colors hover:bg-black/30"
            aria-label="Resize start"
          />
        )}
        <div
          onPointerDown={onDown("move")}
          style={{ touchAction: "none" }}
          className="flex flex-1 cursor-grab items-center active:cursor-grabbing"
        />
        {canResize && (
          <div
            onPointerDown={onDown("resize-end")}
            style={{ width: RESIZE_HANDLE_W, touchAction: "none" }}
            className="shrink-0 cursor-ew-resize border-l border-black/10 bg-black/10 transition-colors hover:bg-black/30"
            aria-label="Resize end"
          />
        )}
      </div>

      {/* Label (priority marker + title).  Renders INSIDE the bar when it
          fits — white text on the coloured body.  Renders OUTSIDE (to
          the right) otherwise — dark text on the lane background, with
          nothing left on top of the bar so the bar reads as a clean
          duration block. */}
      {fitsInside ? (
        <span
          className={`pointer-events-none absolute inset-y-0 left-0 flex items-center gap-1 whitespace-nowrap text-[11px] ${bg.includes("text-white") ? "text-white" : "text-slate-700"}`}
          style={{ paddingLeft: canResize ? RESIZE_HANDLE_W + 4 : 6 }}
        >
          <GanttPriorityMarker priority={item.priority} />
          {item.isFocused && <FocusStar />}
          <span>{titleText}</span>
        </span>
      ) : (
        <span
          className="pointer-events-none absolute inset-y-0 left-full ml-1 flex items-center gap-1 whitespace-nowrap text-[11px] text-slate-700 dark:text-slate-200"
        >
          <GanttPriorityMarker priority={item.priority} />
          {item.isFocused && <FocusStar />}
          <span>{titleText}</span>
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Priority marker shown next to the title in the Gantt.  Same colour
// language as the tree view marker.  High priority gets the gentle pulse.
// ---------------------------------------------------------------------------
// Yellow ★ shown next to the Gantt label when the task is the user's
// current focus.  Kept compact — same scale as the priority marker.
function FocusStar() {
  return (
    <span
      aria-label="Currently focused"
      title="Currently focused"
      className="select-none text-amber-400"
      style={{ fontSize: 12, lineHeight: 1 }}
    >
      ★
    </span>
  );
}

function GanttPriorityMarker({
  priority,
}: {
  priority: "low" | "medium" | "high" | null;
}) {
  if (!priority) return null;
  const colour =
    priority === "high"
      ? "bg-rose-500"
      : priority === "medium"
        ? "bg-amber-500"
        : "bg-sky-500";
  const pulse = priority === "high" ? "animate-priority-pulse" : "";
  return (
    <span
      aria-label={`${priority} priority`}
      className={`inline-block h-3 w-1 rounded-sm ${colour} ${pulse}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { generateKeyBetween } from "fractional-indexing";

function nextSortKeyFor(items: Item[], groupId: string): string {
  let last: string | null = null;
  for (const item of items) {
    if (item.groupId !== groupId) continue;
    if (item.parentId !== null) continue;
    if (last === null || item.sortKey > last) last = item.sortKey;
  }
  return generateKeyBetween(last, null);
}

// ---------------------------------------------------------------------------
// Today view
// ---------------------------------------------------------------------------
//
// Renders a single day's worth of items as a vertically scrollable list,
// grouped by group.  Replaces the chart pane when zoom === "full-day".
//
// Navigation: ← / → buttons step the day, "Today" jumps to the local
// today; on touch, a horizontal swipe (≥ SWIPE_PX in 400 ms, dominant
// horizontal axis) does the same.  We deliberately don't keep the
// horizontal scroller — at one-day-per-screen that gesture lost its
// meaning (the user reported it as "no good"), and a pagination model
// matches the daily-agenda mental model the view is now optimised for.

const SWIPE_PX = 60;
const SWIPE_MAX_MS = 500;

function TodayView({
  groups,
  perGroup,
  selectedDay,
  onPrev,
  onNext,
  onToday,
  onSelect,
  onOpen,
  selectedId,
}: {
  groups: Group[];
  perGroup: Map<string, { dated: DatedItem[]; parked: Item[] }>;
  selectedDay: Date;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  selectedId: string | null;
}) {
  const today = useMemo(todayLocal, []);
  const isToday = selectedDay.getTime() === today.getTime();
  const dateLabel = selectedDay.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year:
      selectedDay.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });

  // Pre-compute which items land on selectedDay, per group.  An item
  // "lands" if selectedDay falls within [start, end] inclusive (markers
  // collapse to a single day so this works for due-only / started-only
  // items too).
  const perDay = useMemo(() => {
    const sel = selectedDay.getTime();
    const map = new Map<string, DatedItem[]>();
    for (const g of groups) {
      const bucket = perGroup.get(g.id);
      if (!bucket) {
        map.set(g.id, []);
        continue;
      }
      const hits = bucket.dated.filter(
        (d) => d.start.getTime() <= sel && d.end.getTime() >= sel,
      );
      map.set(g.id, hits);
    }
    return map;
  }, [groups, perGroup, selectedDay]);

  const totalHits = useMemo(() => {
    let n = 0;
    for (const arr of perDay.values()) n += arr.length;
    return n;
  }, [perDay]);

  // --- Swipe gestures ------------------------------------------------------
  //
  // We track pointerdown's position + time; on pointerup, if the gesture
  // is dominantly horizontal and exceeded the threshold within the time
  // budget, step the day.  Starting on an interactive element opts out
  // so the user can still tap buttons / items normally.
  const swipeStart = useRef<{
    x: number;
    y: number;
    t: number;
    pointerId: number;
  } | null>(null);
  const swipedRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "touch") return;
    const t = e.target as Element;
    if (t.closest("button, a, input, textarea, select")) return;
    swipeStart.current = {
      x: e.clientX,
      y: e.clientY,
      t: Date.now(),
      pointerId: e.pointerId,
    };
    swipedRef.current = false;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || e.pointerId !== start.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = Date.now() - start.t;
    if (dt > SWIPE_MAX_MS) return;
    if (Math.abs(dx) < SWIPE_PX) return;
    if (Math.abs(dx) <= Math.abs(dy)) return; // vertical scroll wins
    swipedRef.current = true;
    if (dx < 0) onNext();
    else onPrev();
  };
  // After a swipe, the pointerup naturally produces a click on whatever
  // sat under the finger when it lifted.  Swallow that one click so a
  // swipe-to-yesterday doesn't accidentally open a task detail pane.
  const onClickCapture = (e: React.MouseEvent) => {
    if (swipedRef.current) {
      swipedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900">
      {/* Date strip */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Previous day"
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          ←
        </button>
        <div className="flex flex-1 flex-col items-center">
          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {dateLabel}
          </span>
          {!isToday && (
            <button
              type="button"
              onClick={onToday}
              className="text-[11px] text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Jump to today
            </button>
          )}
          {isToday && (
            <span className="text-[11px] uppercase tracking-wide text-rose-500">
              Today
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next day"
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          →
        </button>
      </div>

      {/* Item list — swipeable */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          swipeStart.current = null;
        }}
        onClickCapture={onClickCapture}
      >
        {totalHits === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-400 dark:text-slate-500">
            Nothing scheduled for this day.
          </div>
        ) : (
          <ul className="flex flex-col gap-4 p-3">
            {groups.map((g) => {
              const hits = perDay.get(g.id) ?? [];
              if (hits.length === 0) return null;
              return (
                <li key={g.id}>
                  <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {g.name}
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {hits.map((d) => (
                      <TodayItem
                        key={d.item.id}
                        dated={d}
                        selectedDay={selectedDay}
                        selected={selectedId === d.item.id}
                        onSelect={onSelect}
                        onOpen={onOpen}
                      />
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function TodayItem({
  dated,
  selectedDay,
  selected,
  onSelect,
  onOpen,
}: {
  dated: DatedItem;
  selectedDay: Date;
  selected: boolean;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  const { item } = dated;
  // Same colour language as the Gantt bar so the visual idiom is consistent.
  const today = useMemo(todayLocal, []);
  const overdue =
    !item.isComplete && dated.kind !== "started" &&
    dated.end.getTime() < today.getTime();
  let bg = "bg-blue-500 text-white";
  if (item.isComplete) bg = "bg-slate-300 text-slate-600";
  else if (overdue) bg = "bg-rose-500 text-white";
  else if (item.status === "in_progress") bg = "bg-emerald-500 text-white";

  // Subtitle: where this item sits relative to today, only when it's
  // not a one-day affair.  E.g. "May 18 → May 22 · day 1 of 5".
  let rangeLabel: string | null = null;
  if (dated.kind === "bar") {
    const totalDays =
      Math.round(
        (dated.end.getTime() - dated.start.getTime()) / (24 * 60 * 60 * 1000),
      ) + 1;
    const dayIndex =
      Math.round(
        (selectedDay.getTime() - dated.start.getTime()) /
          (24 * 60 * 60 * 1000),
      ) + 1;
    rangeLabel = `${fmtMonthDay(dated.start)} → ${fmtMonthDay(dated.end)} · day ${dayIndex} of ${totalDays}`;
  } else if (dated.kind === "due") {
    rangeLabel = "Due today";
  } else if (dated.kind === "started") {
    rangeLabel = "Started today";
  }

  // Tap → select; double-tap → open the detail pane.  Mirrors the
  // single/double-tap semantics used on Gantt bars (see handleBarTap).
  const lastTap = useRef<number>(0);
  const onClick = () => {
    const now = Date.now();
    if (now - lastTap.current < 350) {
      onOpen(item.id);
      lastTap.current = 0;
    } else {
      onSelect(item.id);
      lastTap.current = now;
    }
  };

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-lg px-3 py-2 text-left text-sm shadow-sm transition-colors ${bg} ${
          selected ? "ring-2 ring-slate-900 ring-offset-1 dark:ring-slate-100" : ""
        } ${item.isComplete ? "line-through" : ""}`}
      >
        <div className="flex items-start gap-2">
          {item.priority && <GanttPriorityMarker priority={item.priority} />}
          {item.isFocused && <FocusStar />}
          <span className="flex-1 break-words font-medium">
            {item.title || (
              <span className="italic opacity-70">(untitled)</span>
            )}
          </span>
        </div>
        {rangeLabel && (
          <div className="mt-1 text-[11px] opacity-80">{rangeLabel}</div>
        )}
      </button>
    </li>
  );
}

function fmtMonthDay(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
